<template>
  <div class="app-container">
    <!-- 顶部工具栏 -->
    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap;">
      <span style="font-size: 16px; font-weight: bold; color: #303133;">
        GESP编程题管理 <span v-if="repoTitle" style="color: #909399; font-size: 13px; font-weight: normal;">— {{ repoTitle }}</span>
      </span>
      <div style="flex: 1;" />
      <el-input
        v-model="keyword"
        placeholder="搜索题号/标题/来源"
        size="small"
        clearable
        style="width: 200px;"
        @keyup.enter.native="fetchList"
        @clear="fetchList"
      />
      <el-select v-model="filterDifficulty" placeholder="难度" size="small" clearable style="width: 100px;" @change="fetchList">
        <el-option label="全部" :value="null" />
        <el-option label="Level 1" :value="1" />
        <el-option label="Level 2" :value="2" />
        <el-option label="Level 3" :value="3" />
        <el-option label="Level 4" :value="4" />
        <el-option label="Level 5" :value="5" />
        <el-option label="Level 6" :value="6" />
      </el-select>
      <el-button size="small" type="primary" icon="el-icon-search" @click="fetchList">查询</el-button>
      <el-button size="small" type="success" icon="el-icon-plus" @click="openAdd">新增</el-button>
      <el-button size="small" type="primary" icon="el-icon-upload2" @click="mdImportDialogVisible = true">markdown导入</el-button>
      <el-button size="small" icon="el-icon-arrow-left" @click="$router.go(-1)">返回</el-button>
    </div>

    <!-- 题目表格 -->
    <el-table
      :data="list"
      border
      fit
      highlight-current-row
      :header-cell-style="{ background: '#f2f3f4', color: '#555', fontWeight: 'bold' }"
      style="width: 100%;"
    >
      <el-table-column type="selection" width="46" align="center" />
      <el-table-column label="序号" type="index" width="60" align="center" />
      <el-table-column prop="number" label="题号" width="180" align="center" show-overflow-tooltip />
      <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
      <el-table-column prop="difficulty" label="难度" width="80" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="row.difficulty" size="mini" :type="difficultyType(row.difficulty)">Level {{ row.difficulty }}</el-tag>
          <span v-else style="color:#999;">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="source" label="来源" width="120" align="center" show-overflow-tooltip />
      <el-table-column prop="timeLimit" label="时限(ms)" width="90" align="center" />
      <el-table-column prop="memoryLimit" label="内存(MB)" width="90" align="center" />
      <el-table-column prop="createdAt" label="创建时间" width="160" align="center" show-overflow-tooltip />
      <el-table-column label="操作" width="140" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" style="color:#409eff;" @click="openEdit(row)">编辑</el-button>
          <el-divider direction="vertical" />
          <el-button type="text" size="small" style="color:#f56c6c;" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div style="margin-top: 16px; display: flex; justify-content: flex-end;">
      <el-pagination
        :current-page="pageNum"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="val => { pageSize = val; fetchList() }"
        @current-change="val => { pageNum = val; fetchList() }"
      />
    </div>

    <!-- markdown 导入对话框 -->
    <el-dialog title="markdown导入" :visible.sync="mdImportDialogVisible" width="420px" :close-on-click-modal="false">
      <el-upload
        class="upload-demo"
        drag
        action=""
        :auto-upload="false"
        :file-list="mdFileList"
        :on-change="handleMdFileChange"
        :on-remove="() => { mdFileList = [] }"
        accept=".md,.markdown"
        :limit="1"
      >
        <i class="el-icon-upload" />
        <div class="el-upload__text">将Markdown文件拖到此处，或<em>点击上传</em></div>
        <div slot="tip" class="el-upload__tip">支持 .md / .markdown</div>
      </el-upload>
      <span slot="footer">
        <el-button @click="mdImportDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="mdParsing" @click="submitMdFile">解 析</el-button>
      </span>
    </el-dialog>

    <!-- markdown 解析结果预览对话框 -->
    <el-dialog title="导入预览" :visible.sync="mdPreviewVisible" width="92%" :close-on-click-modal="false" top="4vh">
      <div style="margin-bottom: 12px; color: #606266; font-size: 13px;">
        共解析到 <strong>{{ mdPreviewList.length }}</strong> 道题，确认后将全部导入到当前题库。可在下方修改后再提交。
      </div>
      <div style="max-height: 72vh; overflow-y: auto; padding-right: 4px;">
        <el-collapse v-model="mdPreviewExpanded" accordion>
          <el-collapse-item
            v-for="(item, idx) in mdPreviewList"
            :key="idx"
            :name="idx"
          >
            <template slot="title">
              <span style="font-weight: bold; color: #303133;">
                {{ idx + 1 }}. {{ item.number || '(无题号)' }}
              </span>
              <span style="margin-left: 12px; color: #606266;">{{ item.title }}</span>
              <el-tag v-if="item.difficulty" size="mini" style="margin-left: 10px;" :type="difficultyType(item.difficulty)">Level {{ item.difficulty }}</el-tag>
            </template>

            <el-form label-width="90px" size="small" style="padding: 0 8px;">
              <el-row :gutter="16">
                <el-col :span="8">
                  <el-form-item label="题号">
                    <el-input v-model="item.number" />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="标题">
                    <el-input v-model="item.title" />
                  </el-form-item>
                </el-col>
                <el-col :span="4">
                  <el-form-item label="难度">
                    <el-select v-model="item.difficulty" placeholder="-" style="width:100%;">
                      <el-option v-for="n in 6" :key="n" :label="'Level ' + n" :value="n" />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="4">
                  <el-form-item label="来源/标签">
                    <el-input v-model="item.source" />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="题目描述">
                <el-input v-model="item.content" type="textarea" :rows="4" />
              </el-form-item>

              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="输入描述">
                    <el-input v-model="item.inputDescription" type="textarea" :rows="2" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="输出描述">
                    <el-input v-model="item.outputDescription" type="textarea" :rows="2" />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="输入格式">
                    <el-input v-model="item.inputFormat" type="textarea" :rows="2" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="输出格式">
                    <el-input v-model="item.outputFormat" type="textarea" :rows="2" />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="数据范围">
                <el-input v-model="item.dataRange" />
              </el-form-item>

              <el-form-item label="参考程序">
                <el-input v-model="item.hint" type="textarea" :rows="4" />
              </el-form-item>

              <el-form-item label="测试用例">
                 <el-input v-model="item.testCases" type="textarea" :rows="3" :placeholder="getTestCaseCount(item.testCases)" />
              </el-form-item>

              <el-row :gutter="16">
                <el-col :span="6">
                  <el-form-item label="时限(ms)">
                    <el-input v-model.number="item.timeLimit" />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item label="内存(MB)">
                    <el-input v-model.number="item.memoryLimit" />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="题目图片">
                <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:flex-start;">
                  <div v-for="(url, pi) in getImageList(item)" :key="pi" style="position:relative;">
                    <img :src="url" style="width:72px;height:72px;object-fit:contain;border:1px solid #eee;border-radius:4px;" />
                    <el-button
                      type="danger" icon="el-icon-delete" circle size="mini"
                      style="position:absolute;top:-8px;right:-8px;width:20px;height:20px;padding:0;"
                      @click="removePreviewImage(item, pi)"
                    />
                  </div>
                  <el-upload
                    :action="'/api/file/uploadTo/gesp-question'"
                    :headers="uploadHeaders"
                    :show-file-list="false"
                    :on-success="(res) => handlePreviewImageSuccess(res, item)"
                    accept="image/*"
                  >
                    <div style="width:72px;height:72px;border:1px dashed #d9d9d9;border-radius:4px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;color:#8c939d;font-size:12px;">
                      <i class="el-icon-plus" style="font-size:18px;" />
                      <span>上传</span>
                    </div>
                  </el-upload>
                </div>
              </el-form-item>
            </el-form>
          </el-collapse-item>
        </el-collapse>
      </div>
      <span slot="footer">
        <el-button @click="mdPreviewVisible = false">取 消</el-button>
        <el-button type="primary" :loading="mdSaving" @click="confirmMdImport">确认导入</el-button>
      </span>
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px" :close-on-click-modal="false">
      <el-form ref="editForm" :model="form" :rules="rules" label-width="100px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="题号" prop="number">
              <el-input v-model="form.number" placeholder="如 GESP120230326" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="难度" prop="difficulty">
              <el-select v-model="form.difficulty" placeholder="请选择" style="width:100%;">
                <el-option v-for="n in 6" :key="n" :label="'Level ' + n" :value="n" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入题目标题" />
        </el-form-item>
        <el-form-item label="题目内容" prop="content">
          <el-input v-model="form.content" type="textarea" :rows="4" placeholder="题目描述" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="输入描述">
              <el-input v-model="form.inputDescription" type="textarea" :rows="2" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="输出描述">
              <el-input v-model="form.outputDescription" type="textarea" :rows="2" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="输入格式">
              <el-input v-model="form.inputFormat" type="textarea" :rows="2" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="输出格式">
              <el-input v-model="form.outputFormat" type="textarea" :rows="2" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="数据范围">
          <el-input v-model="form.dataRange" placeholder="如 1 ≤ n ≤ 1000" />
        </el-form-item>
        <el-form-item label="提示">
          <el-input v-model="form.hint" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="测试用例">
          <el-input v-model="form.testCases" type="textarea" :rows="3" placeholder="JSON格式的测试用例" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="时限(ms)">
              <el-input v-model.number="form.timeLimit" placeholder="1000" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="内存(MB)">
              <el-input v-model.number="form.memoryLimit" placeholder="256" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="来源">
              <el-input v-model="form.source" placeholder="如：真题" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="题目图片">
          <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:flex-start;">
            <div v-for="(url, i) in getImageList(form)" :key="i" style="position:relative;">
              <img :src="url" style="width:80px;height:80px;object-fit:contain;border:1px solid #eee;border-radius:4px;" />
              <el-button
                type="danger" icon="el-icon-delete" circle size="mini"
                style="position:absolute;top:-8px;right:-8px;width:20px;height:20px;padding:0;"
                @click="removeFormImage(i)"
              />
            </div>
            <el-upload
              :action="'/api/file/uploadTo/gesp-question'"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleFormImageSuccess"
              accept="image/*"
            >
              <div style="width:80px;height:80px;border:1px dashed #d9d9d9;border-radius:4px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;color:#8c939d;font-size:12px;">
                <i class="el-icon-plus" style="font-size:20px;" />
                <span>上传图片</span>
              </div>
            </el-upload>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保 存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  pagingGespQuestions,
  addGespQuestion,
  updateGespQuestion,
  deleteGespQuestion,
  saveGespQuestions
} from '@/api/gespQuestion'
import { getGespCodeQuestions } from '@/api/ai'
import { getToken } from '@/utils/auth'

export default {
  name: 'GESPCodeManage',
  data() {
    return {
      repoId: null,
      repoTitle: '',
      keyword: '',
      filterDifficulty: null,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      list: [],
      loading: false,
      saving: false,
      dialogVisible: false,
      isEdit: false,
      form: this.emptyForm(),
      // markdown 导入
      mdImportDialogVisible: false,
      mdFileList: [],
      mdParsing: false,
      mdPreviewVisible: false,
      mdPreviewList: [],
      mdPreviewExpanded: 0,
      mdSaving: false,
      rules: {
        title: [{ required: true, message: '请输入题目标题', trigger: 'blur' }],
        number: [{ required: true, message: '请输入题号', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑题目' : '新增题目'
    },
    uploadHeaders() {
      return { Authorization: getToken() }
    }
  },
  created() {
    const q = this.$route.query
    this.repoId = q.repoId ? Number(q.repoId) : null
    this.repoTitle = q.repoTitle || ''
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        number: '',
        title: '',
        content: '',
        inputDescription: '',
        outputDescription: '',
        inputFormat: '',
        outputFormat: '',
        dataRange: '',
        hint: '',
        testCases: '', // JSON string
        difficulty: null,
        timeLimit: 1000,
        memoryLimit: 256,
        source: '',
        repoId: null
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await pagingGespQuestions({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          keyword: this.keyword || undefined,
          difficulty: this.filterDifficulty || undefined,
          repoId: this.repoId || undefined
        })
        const data = res?.data || {}
        this.list = data.records || []
        this.total = data.total || 0
      } catch (e) {
        this.$message.error('加载失败：' + (e.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.isEdit = false
      this.form = this.emptyForm()
      this.form.repoId = this.repoId
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.editForm && this.$refs.editForm.clearValidate())
    },
    openEdit(row) {
      this.isEdit = true
      this.form = { ...row }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.editForm && this.$refs.editForm.clearValidate())
    },
    async submitForm() {
      const valid = await new Promise(resolve => this.$refs.editForm.validate(resolve))
      if (!valid) return
      this.saving = true
      try {
        if (this.isEdit) {
          await updateGespQuestion(this.form.id, this.form)
          this.$message.success('修改成功')
        } else {
          await addGespQuestion(this.form)
          this.$message.success('新增成功')
        }
        this.dialogVisible = false
        this.fetchList()
      } catch (e) {
        this.$message.error('保存失败：' + (e.message || '未知错误'))
      } finally {
        this.saving = false
      }
    },
    handleDelete(row) {
      this.$confirm(`确定删除题目「${row.title || row.number}」吗？`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(async() => {
        try {
          await deleteGespQuestion(row.id)
          this.$message.success('删除成功')
          this.fetchList()
        } catch (e) {
          this.$message.error('删除失败：' + (e.message || '未知错误'))
        }
      }).catch(() => {})
    },
    difficultyType(level) {
      const map = { 1: 'success', 2: 'success', 3: '', 4: '', 5: 'warning', 6: 'danger' }
      return map[level] || ''
    },
    getImageList(item) {
      if (!item || !item.imagePaths) return []
      try {
        const parsed = JSON.parse(item.imagePaths)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    handleFormImageSuccess(res) {
      const url = res.data || res.url || res
      if (!url) return
      let imgs = this.getImageList(this.form)
      imgs.push(url)
      this.$set(this.form, 'imagePaths', JSON.stringify(imgs))
    },
    removeFormImage(idx) {
      let imgs = this.getImageList(this.form)
      imgs.splice(idx, 1)
      this.$set(this.form, 'imagePaths', imgs.length ? JSON.stringify(imgs) : null)
    },
    handlePreviewImageSuccess(res, item) {
      const url = res.data || res.url || res
      if (!url) return
      let imgs = this.getImageList(item)
      imgs.push(url)
      this.$set(item, 'imagePaths', JSON.stringify(imgs))
    },
    removePreviewImage(item, idx) {
      let imgs = this.getImageList(item)
      imgs.splice(idx, 1)
      this.$set(item, 'imagePaths', imgs.length ? JSON.stringify(imgs) : null)
    },
    getTestCaseCount(jsonStr) {
      if (!jsonStr) return '无'
      try {
        const arr = JSON.parse(jsonStr)
        return Array.isArray(arr) ? (arr.length + ' 个测试用例') : '格式错误'
      } catch (e) {
        return '解析错误'
      }
    },
    handleMdFileChange(file) {
      this.mdFileList = [file]
    },
    parseDifficulty(levelStr) {
      if (!levelStr) return null
      // "GESP 1" -> 1, "GESP 2" -> 2, 兼容 "Level 3" 等格式，取最后一个数字
      const m = String(levelStr).match(/(\d+)\s*$/)
      if (m) return parseInt(m[1])
      const m2 = String(levelStr).match(/(\d+)/)
      return m2 ? parseInt(m2[1]) : null
    },
    buildSampleInput(samples) {
      if (!samples || !samples.length) return ''
      return samples.map((s, i) => {
        let line = `样例${i + 1}：${s.input || ''}`
        if (s.explanation) line += `\n（${s.explanation}）`
        return line
      }).join('\n')
    },
    buildSampleOutput(samples) {
      if (!samples || !samples.length) return ''
      return samples.map((s, i) => `样例${i + 1}：${s.output || ''}`).join('\n')
    },
    parseRawToQuestion(raw) {
      return {
        number: raw.question_id || '',
        title: raw.title || '',
        content: raw.problem_description || raw.chinese_translation || '',
        inputDescription: raw.input_description || '',
        outputDescription: raw.output_description || '',
        inputFormat: raw.input_format || this.buildSampleInput(raw.samples),
        outputFormat: raw.output_format || this.buildSampleOutput(raw.samples),
        dataRange: raw.data_range || '',
        hint: raw.reference_program || '',
        testCases: raw.test_cases ? JSON.stringify(raw.test_cases) : '',
        difficulty: this.parseDifficulty(raw.difficulty_level),
        source: raw.question_tags || '',
        imagePaths: null,
        timeLimit: raw.time_limit || 1000,
        memoryLimit: raw.memory_limit || 256,
        repoId: this.repoId
      }
    },
    async submitMdFile() {
      if (!this.mdFileList || this.mdFileList.length === 0) {
        this.$message.warning('请先选择 Markdown 文件')
        return
      }
      this.mdParsing = true
      try {
        const formData = new FormData()
        formData.append('md_file', this.mdFileList[0].raw)
        const payload = await getGespCodeQuestions(formData)
        const arr = Array.isArray(payload)
          ? payload
          : (payload.problems || payload.data || payload.results || payload.questions || [])
        if (!arr.length) {
          this.$message.warning('未解析到任何题目，请检查文件格式')
          return
        }
        this.mdPreviewList = arr.map(r => this.parseRawToQuestion(r))
        this.mdImportDialogVisible = false
        this.mdFileList = []
        this.mdPreviewVisible = true
      } catch (e) {
        const msg = (e && e.response && e.response.data && (e.response.data.msg || e.response.data.message)) ||
          (e && e.message) || '未知错误'
        this.$message.error('解析失败：' + msg)
      } finally {
        this.mdParsing = false
      }
    },
    async confirmMdImport() {
      if (!this.mdPreviewList.length) return
      this.mdSaving = true
      try {
        await saveGespQuestions(this.mdPreviewList)
        this.$message.success(`成功导入 ${this.mdPreviewList.length} 道题`)
        this.mdPreviewVisible = false
        this.mdPreviewList = []
        this.fetchList()
      } catch (e) {
        this.$message.error('导入失败：' + (e.message || '未知错误'))
      } finally {
        this.mdSaving = false
      }
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
}
</style>
